import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fmtGNF, apiError, openPdf, shareDocument, canShare } from "../lib/api";
import { AmountInput } from "../components/AmountInput";
import { stockBadgeClass } from "../lib/display";
import { useAuth } from "../context/AuthContext";

function ClientPicker({ client, setClient, required }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [newClient, setNewClient] = useState({ name: "", phone: "" });
  const [mode, setMode] = useState("search");

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/clients?search=${encodeURIComponent(search)}`).then((r) => setResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (mode === "new") setClient(newClient.name && newClient.phone ? { name: newClient.name, phone: newClient.phone } : null);
  }, [newClient, mode, setClient]);

  if (client?.id) {
    return (
      <div className="flex items-center gap-2 bg-[#F6F4EF] rounded-xl px-3 py-2.5">
        <span className="text-sm font-semibold flex-1">{client.name} <span className="font-normal text-gray-500">· {client.phone}</span></span>
        <button type="button" data-testid="client-clear-btn" className="text-xs text-[#B4432F]" onClick={() => setClient(null)}>Retirer</button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <button type="button" data-testid="client-mode-search" onClick={() => { setMode("search"); setClient(null); }}
          className={`text-xs px-3 py-1.5 rounded-full ${mode === "search" ? "bg-[#16233D] text-white" : "bg-white border border-[#E0DACE]"}`}>Client existant</button>
        <button type="button" data-testid="client-mode-new" onClick={() => { setMode("new"); setClient(null); }}
          className={`text-xs px-3 py-1.5 rounded-full ${mode === "new" ? "bg-[#16233D] text-white" : "bg-white border border-[#E0DACE]"}`}>Nouveau client</button>
      </div>
      {mode === "search" ? (
        <div className="relative">
          <input data-testid="client-search-input" className="kinput" placeholder="Rechercher par nom ou téléphone" value={search} onChange={(e) => setSearch(e.target.value)} />
          {results.length > 0 && (
            <div className="absolute z-30 mt-1 w-full kcard p-1 max-h-48 overflow-y-auto">
              {results.map((c) => (
                <button type="button" key={c.id} data-testid="client-result-item" onClick={() => { setClient({ id: c.id, name: c.name, phone: c.phone }); setSearch(""); setResults([]); }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F6F4EF] text-sm">
                  <span className="font-semibold">{c.name}</span> <span className="text-gray-500">· {c.phone}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="new-client-name-input" className="kinput" placeholder="Nom du client" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} />
          <input data-testid="new-client-phone-input" className="kinput" placeholder="Téléphone" value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} />
        </div>
      )}
      {required && <p className="text-xs text-[#C99A2E]">Client obligatoire pour une vente à crédit (nom + téléphone).</p>}
    </div>
  );
}

export default function NewSale() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const noStock = user?.company?.stock_mode === "no_stock";
  const isPatron = user?.role === "patron";
  const maxDiscount = isPatron ? null : Number(user?.max_discount_percent || 0);
  const rounding = Number(user?.company?.price_rounding || 0);
  const [lines, setLines] = useState([]);
  const [stockError, setStockError] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [payment, setPayment] = useState("cash");
  const [client, setClient] = useState(null);
  const [amountPaid, setAmountPaid] = useState("");
  const [globalAmount, setGlobalAmount] = useState("");
  const [category, setCategory] = useState("");
  const [discount, setDiscount] = useState({ mode: "percent", value: "", reason: "" });
  const [finalInput, setFinalInput] = useState(null);
  const [busy, setBusy] = useState(false);
  const [doneSale, setDoneSale] = useState(null);

  useEffect(() => {
    if (noStock || search.length < 1) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/products?search=${encodeURIComponent(search)}`).then((r) => setResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [search, noStock]);

  const addProduct = (p) => {
    setLines([...lines, { uid: crypto.randomUUID(), product_id: p.id, name: p.name, qty: 1, unit_price: p.sale_price, catalog_price: p.sale_price, discount_reason: "" }]);
    setSearch(""); setResults([]);
    setFinalInput(null); setDiscount({ mode: "percent", value: "", reason: "" });
  };
  const updLine = (uid, k, v) => {
    setLines(lines.map((l) => (l.uid === uid ? { ...l, [k]: v } : l)));
    setFinalInput(null);
  };
  const gross = noStock
    ? parseFloat(globalAmount) || 0
    : lines.reduce((a, l) => a + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const discountValue = parseFloat(discount.value) || 0;
  const finalTotal = (() => {
    if (discountValue <= 0 || gross <= 0) return gross;
    if (discount.mode === "percent") {
      const raw = gross * (1 - discountValue / 100);
      let r = rounding ? Math.round(raw / rounding) * rounding : raw;
      if (rounding && maxDiscount !== null) {
        const minAllowed = Math.ceil((gross * (100 - maxDiscount)) / 100);
        while (r < minAllowed && r < gross) r += rounding;
      }
      return r;
    }
    return gross - discountValue;
  })();
  const appliedPercent = gross > 0 ? ((gross - finalTotal) / gross) * 100 : 0;
  const requestedPercent = discount.mode === "percent" ? discountValue : appliedPercent;
  const overCap = maxDiscount !== null && discountValue > 0 && requestedPercent > maxDiscount + 0.001;
  const invalidFinal = discountValue > 0 && finalTotal <= 0;
  const noDiscountAllowed = maxDiscount === 0;
  const typedFinal = finalInput !== null ? Number(finalInput || 0) : null;
  const finalAboveGross = typedFinal !== null && gross > 0 && typedFinal > gross;
  const finalIsZero = typedFinal !== null && finalInput !== "" && typedFinal <= 0;
  const finalDisplay = finalInput !== null ? finalInput : (gross > 0 ? String(Math.max(0, Math.round(finalTotal))) : "");
  const setPercent = (v) => { setFinalInput(null); setDiscount({ ...discount, mode: "percent", value: v }); };
  const setFinalPrice = (digits) => {
    setFinalInput(digits);
    const f = Number(digits || 0);
    const cut = gross > 0 && f > 0 && f < gross ? gross - f : 0;
    setDiscount({ ...discount, mode: "amount", value: cut > 0 ? String(cut) : "" });
  };
  const total = finalTotal;
  const missingReason = lines.some((l) => l.catalog_price != null && parseFloat(l.unit_price) < l.catalog_price && !l.discount_reason.trim());

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        payment,
        amount_paid: payment === "credit" ? parseFloat(amountPaid) || 0 : undefined,
        client_id: client?.id, client_name: client?.name, client_phone: client?.phone,
        discount_mode: discountValue > 0 ? discount.mode : null,
        discount_value: discountValue > 0 ? discountValue : 0,
        discount_reason: discount.reason || "",
      };
      if (noStock) {
        body.total = parseFloat(globalAmount);
        body.category = category || null;
      } else {
        body.items = lines.map((l) => ({
          product_id: l.product_id, name: l.name, qty: parseFloat(l.qty), unit_price: parseFloat(l.unit_price),
          discount_reason: l.discount_reason || null,
        }));
      }
      const { data } = await api.post("/sales", body);
      setStockError(null);
      toast.success(`Vente ${data.number} enregistrée ✓`);
      setDoneSale(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.insufficient) setStockError(detail);  // le panier reste intact
      else setStockError(null);
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  if (doneSale) {
    return (
      <div className="max-w-md mx-auto kcard p-6 text-center fade-in" data-testid="sale-success-card">
        <p className="font-display font-bold text-xl mb-1">Vente enregistrée</p>
        <p className="font-mono-doc text-lg text-[#C9793A] mb-4">{doneSale.number}</p>
        <p className="font-display font-bold text-3xl mb-5 tabular-nums">{fmtGNF(doneSale.total)}</p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button data-testid="success-ticket-btn" className="btn-ghost py-2.5" onClick={() => openPdf(doneSale.id, "ticket")}>Ticket PDF</button>
          <button data-testid="success-invoice-btn" className="btn-ghost py-2.5" onClick={() => openPdf(doneSale.id, "invoice")}>Facture PDF</button>
        </div>
        {canShare() && (
          <button data-testid="success-share-btn" className="btn-ghost py-2.5 w-full mb-3"
            onClick={() => shareDocument("ticket", doneSale.id, `Ticket ${doneSale.number}`).catch((e) => toast.error(apiError(e)))}>
            Partager le ticket
          </button>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="success-another-btn" className="btn-ghost py-2.5" onClick={() => { setDoneSale(null); setLines([]); setClient(null); setPayment("cash"); setGlobalAmount(""); setAmountPaid(""); setDiscount({ mode: "percent", value: "", reason: "" }); }}>Nouvelle vente</button>
          <button data-testid="success-done-btn" className="btn-amber py-2.5" onClick={() => navigate("/ventes")}>Terminer</button>
        </div>
      </div>
    );
  }

  const canSubmit = (noStock
    ? parseFloat(globalAmount) > 0 && (payment === "cash" || (client && client.name && client.phone))
    : lines.length > 0 && !missingReason && (payment === "cash" || (client && client.name && client.phone)))
    && !overCap && !invalidFinal && !finalAboveGross && !finalIsZero;

  return (
    <div className="max-w-2xl space-y-4 fade-in" data-testid="new-sale-page">
      <h1 className="font-display font-bold text-2xl">Nouvelle vente</h1>

      {noStock ? (
        <div className="kcard p-5 space-y-3">
          <AmountInput data-testid="global-amount-input" className="text-lg" placeholder="Montant encaissé (GNF)"
            value={globalAmount} onChange={setGlobalAmount} onOverflow={(m) => toast.error(m)} />
          <input data-testid="sale-category-input" className="kinput" placeholder="Catégorie (optionnel)" value={category} onChange={(e) => setCategory(e.target.value)} />
        </div>
      ) : (
        <div className="kcard p-5 space-y-3">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3.5 top-3 text-gray-400" />
            <input data-testid="product-search-input" className="kinput pl-11" placeholder="Rechercher un produit par nom..." value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && (
              <div className="absolute z-30 mt-1 w-full kcard p-1 max-h-56 overflow-y-auto">
                {results.map((p) => (
                  <button type="button" key={p.id} data-testid="product-result-item" onClick={() => addProduct(p)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F6F4EF] text-sm flex justify-between items-center gap-2">
                    <span className="font-semibold">{p.name}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="tabular-nums text-gray-500">{fmtGNF(p.sale_price)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${stockBadgeClass(p.stock_qty, p.alert_threshold)}`}>{p.stock_qty} en stock</span>
                    </span>
                  </button>
                ))}
                {results.length === 0 && (
                  <p data-testid="product-not-found" className="px-3 py-2 text-sm text-gray-500">
                    Produit introuvable. Demandez à votre responsable de le créer dans Stock.
                  </p>
                )}
              </div>
            )}
          </div>

          {lines.map((l) => {
            const below = l.catalog_price != null && parseFloat(l.unit_price) < l.catalog_price;
            return (
              <div key={l.uid} data-testid="sale-line" className="bg-[#F6F4EF]/70 rounded-2xl p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold flex-1">{l.name}</p>
                  <button type="button" data-testid="remove-line-btn" onClick={() => setLines(lines.filter((x) => x.uid !== l.uid))} className="text-[#B4432F]"><Trash size={16} /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Quantité</label>
                    <input data-testid="line-qty-input" type="text" inputMode="numeric" className="kinput amount-input" value={l.qty} onChange={(e) => updLine(l.uid, "qty", e.target.value.replace(/[^\d.]/g, ""))} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Prix unitaire {l.catalog_price != null && <span className="text-gray-400">(catalogue : {fmtGNF(l.catalog_price)})</span>}</label>
                    <AmountInput data-testid="line-price-input" value={String(l.unit_price)}
                      readOnly={!isPatron} disabled={!isPatron}
                      onOverflow={(m) => toast.error(m)}
                      onChange={(v) => updLine(l.uid, "unit_price", v)} />
                  </div>
                </div>
                {below && (
                  <div>
                    <p className="text-xs text-[#B4432F] font-semibold mb-1">Prix sous le catalogue — motif obligatoire</p>
                    <input data-testid="discount-reason-input" className="kinput border-[#B4432F]" placeholder="Motif de la remise (obligatoire)" value={l.discount_reason} onChange={(e) => updLine(l.uid, "discount_reason", e.target.value)} />
                  </div>
                )}
              </div>
            );
          })}
          {lines.length === 0 && <p className="text-sm text-gray-400 text-center py-3">Recherchez un produit pour l'ajouter à la vente.</p>}
        </div>
      )}

      <div className="kcard p-5 space-y-4">
        {stockError && (
          <div data-testid="stock-error-block" className="bg-[#FBE9E5] border border-[#E7C4BB] rounded-2xl p-3.5 space-y-2">
            {stockError.insufficient.map((s) => (
              <p key={s.product_id} data-testid="stock-error-line" className="text-sm font-semibold text-[#B4432F]">
                Stock insuffisant pour {s.name} (disponible : {s.available}).
              </p>
            ))}
            {(can("stock_receive") || can("stock_correct")) ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {can("stock_receive") && (                  <button type="button" data-testid="stock-error-reception-btn" className="btn-ghost text-xs px-3 py-2"
                    onClick={() => navigate(`/stock/${stockError.insufficient[0].product_id}`)}>Réceptionner du stock</button>
                )}
                {can("stock_correct") && (
                  <button type="button" data-testid="stock-error-correction-btn" className="btn-ghost text-xs px-3 py-2"
                    onClick={() => navigate(`/stock/${stockError.insufficient[0].product_id}`)}>Corriger le stock</button>
                )}
              </div>
            ) : (
              <p data-testid="stock-error-seller-note" className="text-xs text-gray-600">
                Prévenez votre responsable : vous ne pouvez pas modifier le stock.
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button type="button" data-testid="payment-cash-btn" onClick={() => setPayment("cash")}
            className={`p-3.5 rounded-2xl border font-display font-semibold transition-colors ${payment === "cash" ? "border-[#2E7D5B] bg-[#E3F0EA] text-[#2E7D5B]" : "border-[#E0DACE] bg-white"}`}>
            Payé maintenant
          </button>
          <button type="button" data-testid="payment-credit-btn" onClick={() => setPayment("credit")}
            className={`p-3.5 rounded-2xl border font-display font-semibold transition-colors ${payment === "credit" ? "border-[#C99A2E] bg-[#F7EFD9] text-[#9a7620]" : "border-[#E0DACE] bg-white"}`}>
            À crédit
          </button>
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">Client {payment === "cash" && <span className="font-normal text-gray-400">(optionnel)</span>}</p>
          <ClientPicker client={client} setClient={setClient} required={payment === "credit"} />
        </div>
        {payment === "credit" && (
          <AmountInput data-testid="credit-amount-paid-input" placeholder="Montant déjà reçu (0 si rien)"
            value={amountPaid} onChange={setAmountPaid} onOverflow={(m) => toast.error(m)} />
        )}
        <div className="pt-2 border-t border-[#E9E4DA] space-y-3">
          <div>
            <p className="text-sm font-semibold mb-1">Prix final</p>
            <p className="text-xs text-gray-500 mb-2">
              {maxDiscount === null
                ? "Aucune limite : vous êtes le patron."
                : noDiscountAllowed
                  ? "Aucune remise autorisée."
                  : `Remise maximale autorisée : ${maxDiscount} %.`}
              {rounding > 0 && !noDiscountAllowed ? ` Le prix calculé depuis une remise en % est arrondi à ${rounding.toLocaleString("fr-FR")} GNF.` : ""}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Prix final</label>
                <AmountInput data-testid="final-price-input" value={finalDisplay}
                  readOnly={noDiscountAllowed} disabled={noDiscountAllowed}
                  onOverflow={(m) => toast.error(m)} onChange={setFinalPrice} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Remise (%) — optionnel</label>
                <input data-testid="discount-value-input" type="number" min="0" step="0.1" className="kinput"
                  placeholder="0" disabled={noDiscountAllowed}
                  value={discount.mode === "percent" ? discount.value : (discountValue > 0 ? appliedPercent.toFixed(1) : "")}
                  onChange={(e) => setPercent(e.target.value)} />
              </div>
            </div>
            {discountValue > 0 && (
              <input data-testid="discount-reason-global-input" className="kinput mt-2" placeholder="Motif de la remise (optionnel)"
                value={discount.reason} onChange={(e) => setDiscount({ ...discount, reason: e.target.value })} />
            )}
            {gross > 0 && discountValue > 0 && (
              <p data-testid="discount-live-info" className={`text-xs mt-1.5 ${overCap ? "text-[#B4432F] font-semibold" : "text-gray-500"}`}>
                Remise : {appliedPercent.toFixed(1)} %
                {maxDiscount !== null ? ` (maximum autorisé : ${maxDiscount} %)` : ""}
              </p>
            )}
            {overCap && (
              <p data-testid="discount-cap-warning" className="text-xs text-[#B4432F] mt-1.5 font-semibold">
                Remise refusée : votre remise maximale autorisée est de {maxDiscount} %.
              </p>
            )}
            {invalidFinal && (
              <p data-testid="discount-zero-warning" className="text-xs text-[#B4432F] mt-1.5 font-semibold">
                Le prix final doit rester supérieur à 0.
              </p>
            )}
            {(finalAboveGross || finalIsZero) && (
              <p data-testid="final-price-error" className="text-xs text-[#B4432F] mt-1.5 font-semibold">
                {finalAboveGross
                  ? "Le prix final ne peut pas dépasser le total catalogue."
                  : "Le prix final doit rester supérieur à 0."}
              </p>
            )}
            {noDiscountAllowed && (
              <p data-testid="no-discount-note" className="text-xs text-gray-400 mt-1.5">Aucune remise autorisée</p>
            )}
          </div>
          {discountValue > 0 && (
            <div data-testid="discount-recap" className="text-sm space-y-1">
              <div className="flex items-center justify-between text-gray-500">
                <span>Total avant remise</span>
                <span data-testid="sale-gross-total" className="tabular-nums">{fmtGNF(gross)}</span>
              </div>
              <div className="flex items-center justify-between text-[#B4432F]">
                <span>Remise ({appliedPercent.toFixed(1)} %)</span>
                <span data-testid="sale-discount-amount" className="tabular-nums">- {fmtGNF(gross - finalTotal)}</span>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="font-display font-bold text-lg">Total à payer</span>
            <span data-testid="sale-total" className="font-display font-bold text-2xl tabular-nums">{fmtGNF(total)}</span>
          </div>
        </div>
        <button data-testid="sale-submit-btn" className="btn-amber w-full py-3.5 text-base" disabled={!canSubmit || busy} onClick={submit}>
          {busy ? "Enregistrement..." : "Valider la vente"}
        </button>
      </div>
    </div>
  );
}
